/**
 * AdminFormPageV2 — redesigned create/edit form for admin users.
 *
 * Sections:
 *   01 Basic — name + email
 *   02 Password — password + confirmation. Required on create,
 *                 optional on edit (left blank = keep current).
 *   03 Roles — toggle-chip list (same recipe as the role-permission
 *              chips, no MUI multi-select).
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  fetchOneAdmin,
  saveAdmin,
} from '../../../../actions/adminsActions';
import { fetchAllRoles } from '../../../../actions/rolesActions';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import { preventEnterFormSubmission } from '../../../../utils/formUtils';
import { Button, cn } from '../../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  RailCard,
  RailCardHead,
} from '../../../redesign/edit';
import AdminAvatar from './AdminAvatar';

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

/* ─── Role toggle chip (same recipe as the permission chip) ─── */

function RoleToggle({ label, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn('flex items-center text-left transition-all duration-150')}
      style={{
        gap: 10,
        padding: '8px 12px',
        background: selected ? '#75A2931A' : '#FAF4EA',
        border: `1px solid ${selected ? '#75A293' : '#E8E0CF'}`,
        borderRadius: 8,
        cursor: 'pointer',
        fontFamily: 'inherit',
        width: '100%',
        minWidth: 0,
      }}
      onMouseEnter={(e) => {
        if (selected) return;
        e.currentTarget.style.borderColor = '#75A293';
        e.currentTarget.style.background = '#FAF7F1';
      }}
      onMouseLeave={(e) => {
        if (selected) return;
        e.currentTarget.style.borderColor = '#E8E0CF';
        e.currentTarget.style.background = '#FAF4EA';
      }}
    >
      <span
        aria-hidden
        className="grid place-items-center shrink-0"
        style={{
          width: 16,
          height: 16,
          borderRadius: 4,
          background: selected ? '#75A293' : 'transparent',
          border: `1.5px solid ${selected ? '#75A293' : '#C8BFA8'}`,
          color: '#fff',
          fontSize: 11,
          lineHeight: 1,
        }}
      >
        {selected && '✓'}
      </span>
      <span
        className="flex-1 min-w-0 truncate text-navy"
        style={{ fontSize: 13 }}
      >
        {label}
      </span>
    </button>
  );
}

/* ─── Main ────────────────────────────────────────────────────── */

export default function AdminFormPageV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [admin, setAdmin] = useState({
    admin_id: 0,
    admin_name: '',
    admin_email: '',
    admin_password: '',
    admin_password_confirmation: '',
    roles: [],
  });

  const loading = useSelector((s) => s.admins.loading);
  const adminStore = useSelector((s) => s.admins.admin);
  const roles = useSelector((s) => s.roles.roles);

  useEffect(() => {
    dispatch(fetchAllRoles());
  }, [dispatch]);

  useEffect(() => {
    if (!id) return;
    if (!adminStore || adminStore.admin_id !== parseInt(id, 10)) {
      dispatch(fetchOneAdmin(id));
      return;
    }
    setAdmin({
      admin_id: adminStore.admin_id || 0,
      admin_name: adminStore.admin_name || '',
      admin_email: adminStore.admin_email || '',
      admin_password: '',
      admin_password_confirmation: '',
      roles: adminStore.roles
        ? adminStore.roles.map((r) => r.role_id)
        : [],
    });
  }, [id, adminStore, dispatch]);

  const handleChange = (e) => {
    setAdmin((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleRole = (roleId) => {
    setAdmin((prev) => ({
      ...prev,
      roles: prev.roles.includes(roleId)
        ? prev.roles.filter((rid) => rid !== roleId)
        : [...prev.roles, roleId],
    }));
  };

  const handleSave = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (
      admin.admin_id === 0 &&
      admin.admin_password !== admin.admin_password_confirmation
    ) {
      Swal.fire({
        icon: 'error',
        title: 'Password mismatch',
        text: 'Password and confirmation do not match',
      });
      return;
    }
    dispatch(saveAdmin(admin, () => navigate('/admins')));
  };

  const isEdit = admin.admin_id > 0;
  const eyebrow = isEdit ? 'Editing admin' : 'New admin';
  const title = isEdit ? admin.admin_name || `Admin #${id}` : 'New admin';

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={!!loading} />
      <div
        className="sticky top-0 bg-bg border-b border-sand"
        style={{ zIndex: 20 }}
      >
        <div
          className="flex items-start"
          style={{ padding: '20px 28px', gap: 16, flexWrap: 'wrap' }}
        >
          <div className="flex-1 min-w-0">
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: '0.08em' }}
            >
              {eyebrow}
            </div>
            <h1
              className={cn(
                'text-navy truncate',
                !admin.admin_name && isEdit && 'text-text-light italic',
              )}
              style={{
                fontSize: 28,
                fontWeight: 700,
                margin: '4px 0 4px',
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
              }}
              title={title}
            >
              {title}
            </h1>
            <div className="font-mono text-text-muted" style={{ fontSize: 12 }}>
              {isEdit ? (
                <>admin_id · <strong className="text-navy">{id}</strong></>
              ) : (
                <span>Fill the fields below and click Create.</span>
              )}
            </div>
          </div>
          <div className="flex items-center" style={{ gap: 8 }}>
            <Link to="/admins" className="no-underline">
              <Button variant="ghost">Discard Changes</Button>
            </Link>
            <Button
              variant="coral"
              type="submit"
              form="admin-form"
              leadingIcon={SaveIconSvg}
              disabled={loading}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>

      <div
        className="edit-canvas grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] items-start"
        style={{ gap: 24, padding: '24px 32px 60px' }}
      >
        <form
          id="admin-form"
          onSubmit={handleSave}
          onKeyDown={preventEnterFormSubmission}
          autoComplete="off"
          style={{ minWidth: 0 }}
        >
          <FormSection title="Basic" num="01">
            <FieldRow>
              <FieldBlock label="Name" required htmlFor="f-admin-name" noMargin>
                <TextInput
                  id="f-admin-name"
                  name="admin_name"
                  value={admin.admin_name || ''}
                  onChange={handleChange}
                  required
                  autoFocus
                />
              </FieldBlock>
              <FieldBlock label="Email" required htmlFor="f-admin-email" noMargin>
                <TextInput
                  id="f-admin-email"
                  name="admin_email"
                  type="email"
                  value={admin.admin_email || ''}
                  onChange={handleChange}
                  required
                />
              </FieldBlock>
            </FieldRow>
          </FormSection>

          <FormSection
            title="Password"
            num="02"
            right={
              <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                {isEdit
                  ? 'Leave blank to keep current'
                  : 'Required on create'}
              </span>
            }
          >
            <FieldRow>
              <FieldBlock
                label="Password"
                required={!isEdit}
                htmlFor="f-admin-password"
                noMargin
              >
                <TextInput
                  id="f-admin-password"
                  name="admin_password"
                  type="password"
                  autoComplete="new-password"
                  value={admin.admin_password || ''}
                  onChange={handleChange}
                  required={!isEdit}
                />
              </FieldBlock>
              <FieldBlock
                label="Confirm password"
                required={!isEdit}
                htmlFor="f-admin-password-confirm"
                noMargin
              >
                <TextInput
                  id="f-admin-password-confirm"
                  name="admin_password_confirmation"
                  type="password"
                  autoComplete="new-password"
                  value={admin.admin_password_confirmation || ''}
                  onChange={handleChange}
                  required={!isEdit}
                />
              </FieldBlock>
            </FieldRow>
          </FormSection>

          <FormSection
            title="Roles"
            num="03"
            right={
              <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                {admin.roles.length} selected
              </span>
            }
          >
            {!Array.isArray(roles) || roles.length === 0 ? (
              <div
                className="bg-surface border border-sand text-text-muted italic text-center"
                style={{ borderRadius: 8, padding: '12px 14px', fontSize: 13 }}
              >
                {!roles ? 'Loading roles…' : 'No roles defined yet — create roles first.'}
              </div>
            ) : (
              <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                style={{ gap: 8 }}
              >
                {roles.map((r) => (
                  <RoleToggle
                    key={r.role_id}
                    label={r.role_name}
                    selected={admin.roles.includes(r.role_id)}
                    onToggle={() => toggleRole(r.role_id)}
                  />
                ))}
              </div>
            )}
          </FormSection>
        </form>

        {/* Right rail — read-only profile info card. Photo lives in
         *  adminStore (this form doesn't edit it); the name/email/
         *  role-count come from the live form state so the card
         *  updates as the admin types. */}
        <aside
          className="edit-rail"
          style={{
            alignSelf: 'start',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minWidth: 0,
          }}
        >
          <RailCard
            head={({ open, toggle }) => (
              <RailCardHead
                eyebrow="Profile"
                open={open}
                onToggle={toggle}
              />
            )}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: 18,
                gap: 12,
              }}
            >
              <AdminAvatar
                id={admin.admin_id || 0}
                name={admin.admin_name}
                image={adminStore?.admin_image}
                size={96}
              />
              <div style={{ textAlign: 'center', minWidth: 0, width: '100%' }}>
                <div
                  className="font-semibold text-navy truncate"
                  style={{ fontSize: 15, lineHeight: 1.25 }}
                  title={admin.admin_name || ''}
                >
                  {admin.admin_name || (
                    <span className="text-text-light italic">No name yet</span>
                  )}
                </div>
                <div
                  className="font-mono text-text-muted truncate"
                  style={{ fontSize: 11.5, marginTop: 4 }}
                  title={admin.admin_email || ''}
                >
                  {admin.admin_email || '—'}
                </div>
              </div>
              <div
                className="w-full"
                style={{
                  borderTop: '1px solid #E8E0CF',
                  paddingTop: 12,
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11.5,
                }}
              >
                <span className="font-condensed font-bold uppercase text-text-muted" style={{ letterSpacing: '0.08em' }}>
                  Roles
                </span>
                <span className="font-mono text-navy">
                  {admin.roles.length}
                </span>
              </div>
              {isEdit && (
                <div
                  className="w-full"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 11.5,
                  }}
                >
                  <span className="font-condensed font-bold uppercase text-text-muted" style={{ letterSpacing: '0.08em' }}>
                    admin_id
                  </span>
                  <span className="font-mono text-navy">{id}</span>
                </div>
              )}
            </div>
          </RailCard>
        </aside>
      </div>
    </div>
  );
}
